import { Alert, Box, Typography } from '@mui/material';
import { DataGrid, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { canManageOrg } from 'client/modules/Auth/AuthReducer';
import Filters from 'client/modules/People/pages/List/Filters';
import SecondaryNavTabs from 'client/modules/Surveys/pages/DashboardPage/SecondaryNavTabs';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Layout from '../../../App/components/Layout/Layout';
import { useGetPeopleQuery } from '../../peopleApiSlice';

const messages = defineMessages({
  title: {
    id: 'People.title',
    defaultMessage: 'People',
  },
  subtitle: {
    id: 'People.subtitle',
    defaultMessage: 'This is a list of all people that you have added to Promoter Ninja.',
  },
  unsubscribed: {
    id: 'People.status.unsubscribed',
    defaultMessage: 'Unsubscribed',
  },
  blocked: {
    id: 'People.status.blocked',
    defaultMessage: 'Bounced',
  },
  sent: {
    id: 'People.status.sent',
    defaultMessage: 'Sent',
  },
  scheduled: {
    id: 'People.status.scheduled',
    defaultMessage: 'Scheduled',
  },
  skipped: {
    id: 'People.status.skipped',
    defaultMessage: 'Skipped',
  },
  blocklisted: {
    id: 'People.status.blocklisted',
    defaultMessage: 'Blocklisted',
  },
  created: {
    id: 'People.status.created',
    defaultMessage: 'Created',
  },
  people: {
    id: 'People.people',
    defaultMessage: '{count} people',
  },
  email: {
    id: 'People.table.email',
    defaultMessage: 'Email,',
  },
  name: {
    id: 'People.table.name',
    defaultMessage: 'Name,',
  },
  status: {
    id: 'People.table.status',
    defaultMessage: 'Status,',
  },
  backToPeople: {
    id: 'Person.Back',
    defaultMessage: 'Back to people',
  },
  searchEmail: {
    id: 'Search.Email',
    defaultMessage: 'Email starts with...',
  },
  listLoadError: {
    id: 'People.listLoadError',
    defaultMessage: 'Could not load people: {detail}',
  },
});

function CustomToolbar() {
  const { formatMessage } = useIntl();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        p: 0.5,
      }}
    >
      <GridToolbarQuickFilter placeholder={formatMessage(messages.searchEmail)} />
    </Box>
  );
}

const MIN_PAGE_SIZE = 1;
const MAX_PAGE_SIZE = 500;
const DEFAULT_PAGE_SIZE = 10;

const clampPageSize = size =>
  Math.min(MAX_PAGE_SIZE, Math.max(MIN_PAGE_SIZE, Number(size) || DEFAULT_PAGE_SIZE));

const renderLink = (survey, status, formatMessage) => {
  function PeopleLinkCell(params) {
    return params.value == null ? (
      ''
    ) : (
      <Link
        to={`/surveys/${survey}/people/${params.row.id}`}
        tabIndex={params.tabIndex}
        state={{
          returnLinkTo: 'back',
          returnLinkText: formatMessage(messages.backToPeople),
        }}
      >
        {params.value}
      </Link>
    );
  }
  PeopleLinkCell.displayName = 'PeopleLinkCell';
  return PeopleLinkCell;
};

function PeopleList() {
  const navigate = useNavigate();
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [skip, setSkip] = useState(0);
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status') || 'active';
  const quickFilterValues =
    searchParams
      .get('q')
      ?.split('+')
      .filter(v => v) || [];
  const filter = { status };
  if (!isEmpty(quickFilterValues)) {
    filter.query = quickFilterValues.join(' ');
  }
  const [sortModel, setSortModel] = React.useState([
    {
      field: 'email',
      sort: 'asc',
    },
  ]);
  const { id: survey } = useParams();
  const { formatMessage, locale } = useIntl();
  const canManage = useSelector(canManageOrg);

  const { data, isFetching, isError, error } = useGetPeopleQuery({
    survey,
    skip,
    limit,
    sort: sortModel.reduce(
      (acc, curr) => acc + (acc ? ',' : '') + (curr.sort === 'desc' ? '-' : '') + curr.field,
      '',
    ),
    filter: JSON.stringify(filter),
  });

  // mongoose-paginate-v2 uses totalDocs; keep total for API backward compatibility
  const totalPeople = data?.totalDocs ?? data?.total ?? 0;

  const onFilterChange = newFilter => {
    navigate({ search: `q=${newFilter.quickFilterValues.join('+')}` });
  };

  const statusFormatter = p => {
    return p.value.at
      ? `${formatMessage(messages[p.value.status])} ${moment.unix(p.value.at).locale(locale).fromNow()}`
      : '';
  };
  const columns = [
    {
      field: 'email',
      headerName: formatMessage(messages.email),
      minWidth: 250,
      flex: 1.5,
      editable: false,
      renderCell: renderLink(survey, status, formatMessage),
    },
    {
      field: 'name',
      headerName: formatMessage(messages.name),
      minWidth: 200,
      flex: 1,
      editable: false,
      renderCell: renderLink(survey, status, formatMessage),
    },
    {
      field: 'status',
      headerName: formatMessage(messages.status),
      minWidth: 200,
      flex: 1,
      editable: false,
      sortable: false,
      valueFormatter: statusFormatter,
      valueGetter: ({ row }) => {
        // Collect all possible statuses with their timestamps
        const statuses = [];

        if (row.blocklisted_at) {
          statuses.push({
            status: 'blocklisted',
            at: row.blocklisted_at,
          });
        }
        if (row.unsubscribed_at) {
          statuses.push({
            status: 'unsubscribed',
            at: row.unsubscribed_at,
          });
        }
        if (row.blocked_at) {
          statuses.push({
            status: 'blocked',
            at: row.blocked_at,
          });
        }
        if (row.last_sent_at) {
          statuses.push({
            status: 'sent',
            at: row.last_sent_at,
          });
        }
        if (row.next_survey_scheduled_at) {
          statuses.push({
            status: 'scheduled',
            at: row.next_survey_scheduled_at,
          });
        }
        if (row.skipped?.at) {
          statuses.push({
            status: 'skipped',
            at: row.skipped.at,
          });
        }

        // Always include created status as fallback
        statuses.push({
          status: 'created',
          at: row.created_at,
        });

        // Return the status with the most recent timestamp
        return statuses.reduce((latest, current) => (current.at > latest.at ? current : latest));
      },
    },
  ];

  return (
    <Layout secondaryNav={<SecondaryNavTabs survey={survey} />}>
      <Box
        sx={{
          display: 'flex',
          maxWidth: 1280,
          width: '100%',
          alignSelf: 'center',
          mt: 3,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h1">{formatMessage(messages.title)}</Typography>
          <Typography>{formatMessage(messages.subtitle)}</Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          maxWidth: 1280,
          width: '100%',
          alignSelf: 'center',
          mb: 4,
          gap: 2,
        }}
      >
        <Box sx={{ flexGrow: 1, flexBasis: '15%', marginTop: 6 }}>
          <Filters />
        </Box>
        <Box
          sx={{
            flexGrow: 4,
            flexBasis: '85%',
            minHeight: '65vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {isError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formatMessage(messages.listLoadError, {
                detail: error?.data?.message || String(error?.status || ''),
              })}
            </Alert>
          ) : null}
          <Typography variant="h2" color="muted.main" sx={{ fontWeight: 400 }}>
            {formatMessage(messages.people, { count: totalPeople })}
          </Typography>
          <DataGrid
            sx={{
              '& a, & a:hover': {
                color: 'inherit',
              },
            }}
            disableRowSelectionOnClick
            slots={{ toolbar: CustomToolbar }}
            density="compact"
            disableColumnMenu
            disableColumnFilter={!canManage}
            disableColumnSorting={!canManage}
            disableMultipleRowSelection
            pageSizeOptions={[10, 25, 50, 100]}
            paginationMode="server"
            filterMode="server"
            filterModel={{
              items: [],
              logicOperator: 'and',
              quickFilterLogicOperator: 'and',
              quickFilterValues,
            }}
            onFilterModelChange={onFilterChange}
            sortingMode="server"
            rowCount={totalPeople}
            loading={isFetching}
            paginationModel={{
              page: limit > 0 ? Math.floor(skip / limit) : 0,
              pageSize: clampPageSize(limit),
            }}
            onPaginationModelChange={({ page: newPage, pageSize: newSize }) => {
              const safeSize = clampPageSize(newSize);
              setLimit(safeSize);
              setSkip(newPage * safeSize);
            }}
            rows={data?.docs || []}
            columns={columns}
            sortModel={sortModel}
            onSortModelChange={newSortModel => setSortModel(newSortModel)}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
          />
        </Box>
      </Box>
    </Layout>
  );
}

export default PeopleList;
